import { type CSSProperties, useEffect, useRef, useState } from "react";
import { cn } from "../../lib/utils";

export type Tab<T extends string = string> = {
  value: T;
  label: string;
  badge: boolean | null;
  /** Rendered after the label — signals that the tab leads off-site. */
  icon: React.ReactNode | null;
  className?: string;
  activeClassName?: string;
  inactiveClassName?: string;
  activeStyle?: CSSProperties;
  inactiveStyle?: CSSProperties;
  indicatorStyle?: CSSProperties;
};

export function Tabs<T extends string>({
  tabs,
  activeTab,
  onTabChange,
  className,
  stuckClassName,
  unstuckClassName,
}: {
  tabs: Tab<T>[];
  activeTab: T;
  onTabChange: (tab: T) => void;
  className?: string;
  /** Applied when the Tabs is stuck (sticky active). */
  stuckClassName?: string;
  /** Applied when the Tabs is not stuck (in normal flow). */
  unstuckClassName?: string;
}): React.JSX.Element {
  const sentinelRef = useRef<HTMLDivElement>(null);
  const tabsRef = useRef<HTMLDivElement>(null);
  const [isStuck, setIsStuck] = useState(false);

  useEffect(() => {
    const sentinel = sentinelRef.current;
    const tabsEl = tabsRef.current;
    if (sentinel === null || tabsEl === null) return;

    // Find the scroll container (closest ancestor with overflow scroll/auto)
    let scrollRoot: Element | null = tabsEl.parentElement;
    while (scrollRoot !== null) {
      const style = getComputedStyle(scrollRoot);
      if (style.overflowY === "auto" || style.overflowY === "scroll") break;
      scrollRoot = scrollRoot.parentElement;
    }

    // Compute the sticky offset from the CSS top value
    const stickyTop = Number.parseFloat(getComputedStyle(tabsEl).top) || 0;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry !== undefined) {
          setIsStuck(!entry.isIntersecting);
        }
      },
      {
        root: scrollRoot,
        rootMargin: `-${stickyTop}px 0px 0px 0px`,
        threshold: 0,
      }
    );

    observer.observe(sentinel);
    return () => {
      observer.disconnect();
    };
  }, []);

  return (
    <>
      <div ref={sentinelRef} className="h-0 w-full" />
      <div
        ref={tabsRef}
        className={cn(
          "relative flex w-full overflow-x-auto scrollbar-hidden md:overflow-hidden transition-colors duration-200 border-y border-black/10",
          isStuck
            ? (stuckClassName ?? "bg-white/90 backdrop-blur-md")
            : (unstuckClassName ?? "bg-white"),
          className
        )}
      >
        {tabs.map((tab) => {
          const isActive = tab.value === activeTab;

          return (
            <button
              key={tab.value}
              type="button"
              className={cn(
                "relative flex-1 py-3.5 text-center text-sm font-bold cursor-pointer transition-colors duration-200",
                isActive ? "text-primary-black" : "text-secondary-bluegrey-4",
                tab.className,
                isActive ? tab.activeClassName : tab.inactiveClassName
              )}
              style={isActive ? tab.activeStyle : tab.inactiveStyle}
              onClick={() => {
                onTabChange(tab.value);
              }}
            >
              <span className="inline-flex items-center gap-1.5">
                {tab.badge === true && (
                  <span className="relative flex size-1.5">
                    <span className="absolute inline-flex size-full animate-ping rounded-full bg-ds-strawberry-400 opacity-75" />
                    <span className="relative inline-flex size-full rounded-full bg-ds-strawberry-500" />
                  </span>
                )}
                {tab.label}
                {tab.icon}
              </span>

              {isActive && (
                <div
                  className="absolute -bottom-px inset-x-0  z-10 h-0.75 bg-primary-black"
                  style={tab.indicatorStyle}
                />
              )}
            </button>
          );
        })}
      </div>
    </>
  );
}
